import {
  gridLedStandbyPlanningReportProjectionSchema,
  type GridLedStandbyPlanningReportProjection,
} from '../../types/gridLedStandbyPlanningProjection';

export interface GridLedStandbyPlanningReportInput {
  gridLedStandbyPlanningProjection?: unknown;
  gridLedStandbyPlanningAtGen?: unknown;
}

export function gridLedStandbyPlanningReportHasContent(report?: GridLedStandbyPlanningReportInput | null): boolean {
  return report?.gridLedStandbyPlanningProjection != null || report?.gridLedStandbyPlanningAtGen != null;
}

/** Render only the strict public scalar allowlist. The private snapshot is
 * opaque here: its presence can require regeneration, never supply a fallback. */
export function readGridLedStandbyPlanningReportProjection(
  report: GridLedStandbyPlanningReportInput,
): GridLedStandbyPlanningReportProjection | null {
  if (!gridLedStandbyPlanningReportHasContent(report)) return null;
  const parsed = gridLedStandbyPlanningReportProjectionSchema.safeParse(report.gridLedStandbyPlanningProjection);
  if (!parsed.success) {
    throw new Error('The saved grid-led standby assessment has no valid frozen report projection. Regenerate the report before viewing or exporting it.');
  }
  return parsed.data;
}

export interface GridLedStandbyPlanningReportContent {
  headline: string;
  authority: string;
  sections: Array<{ heading: string; rows: Array<[string, string]> }>;
  citations: string[];
  limitations: string[];
  inputHash: string;
  resultHash: string;
}

/** String(number) preserves tiny nonzero values and the exact stored scalar;
 * display rounding must not convert an unknown or a small result into zero. */
function value(number: number | null, unit = ''): string {
  return number === null ? 'Unknown' : `${String(number)}${unit ? ` ${unit}` : ''}`;
}

function check(status: 'pass' | 'fail' | 'indeterminate'): string {
  return status === 'pass' ? 'Pass' : status === 'fail' ? 'Fail' : 'Indeterminate';
}

/** Shared frozen disclosure for browser/native PDF and Office renderers.
 * No live Site reads, calculations, legacy carrier substitution or source
 * quotation fields are allowed at this boundary. */
export function buildGridLedStandbyPlanningReportContent(
  candidate: GridLedStandbyPlanningReportProjection,
): GridLedStandbyPlanningReportContent {
  const projection = gridLedStandbyPlanningReportProjectionSchema.parse(candidate);
  const { annual, standby, ups, sourceBasis } = projection;
  return {
    headline: `Static planning screen: ${check(projection.staticCapacityVerdict)}. Engineering qualification: Indeterminate.`,
    authority: 'User-declared planning inputs; reviewed document values and operator assumptions are not independently verified utility commitments or engineering certification.',
    sections: [
      ...projection.phases.map(phase => ({
        heading: `Phase ${phase.phase} — static capacity and schedule`,
        rows: [
          ['Facility demand', value(phase.facilityMW, 'MW')],
          ['IT demand', value(phase.itMW, 'MW')],
          ['Declared grid import', value(phase.declaredGridImportMW, 'MW')],
          ['Grid service date', phase.gridServiceDate ?? 'Unknown'],
          ['Operation target date', phase.operationTargetDate ?? 'Unknown'],
          ['Modeled normal grid-import margin', value(phase.modeledNormalGridImportMarginMW, 'MW')],
          ['Grid capacity check', check(phase.gridCapacityCheck)],
          ['Grid schedule check', check(phase.gridScheduleCheck)],
          ['Modeled standby margin after declared unit outages', value(phase.modeledStandbyUnitOutMarginMW, 'MW')],
          ['Standby capacity check', check(phase.standbyCapacityCheck)],
          ['Ideal UPS duration', value(phase.idealUpsMinutes, 'minutes')],
          ['UPS power check', check(phase.upsPowerCheck)],
          ['UPS usable-energy basis check', check(phase.upsEnergyBasisCheck)],
        ] satisfies Array<[string, string]>,
      })),
      {
        heading: 'Standby and UPS basis',
        rows: [
          ['Standby role', 'Emergency standby and testing only'],
          ['Standby rating basis', 'Site-net power'],
          ['Standby units', value(standby.unitCount)],
          ['Site-net power per unit', value(standby.siteNetMWPerUnit, 'MW')],
          ['Declared unavailable units', value(standby.unavailableUnits)],
          ['Installed standby power', value(standby.installedMW, 'MW')],
          ['Unavailable standby power', value(standby.unavailableMW, 'MW')],
          ['Remaining standby power', value(standby.remainingMW, 'MW')],
          ['UPS discharge power', value(ups.dischargeMW, 'MW')],
          ['UPS usable energy', value(ups.usableEnergyMWh, 'MWh')],
        ],
      },
      {
        heading: 'Annual energy and accounting inputs',
        rows: [
          ['IT design power', value(annual.itDesignMW, 'MW')],
          ['IT utilization', value(annual.itUtilizationPct, '%')],
          ['PUE', value(annual.pue)],
          ['Annual operating hours', value(annual.hoursPerYear, 'hours/year')],
          ['Comparison PUE', value(annual.comparisonPue)],
          ['Declared grid CO2e factor', value(annual.gridTonnesCo2ePerMWh, 'tCO2e/MWh')],
          ['Standby test load', value(annual.testLoadPct, '%')],
          ['Standby test duration', value(annual.testHoursPerYear, 'hours/year')],
          ['Testing duty', 'Load bank; no grid displacement'],
          ['Declared fuel-use factor', value(annual.litresPerMWh, 'L/MWh')],
          ['Declared fuel CO2e factor', value(annual.kgCo2ePerLitre, 'kgCO2e/L')],
        ],
      },
      {
        heading: 'Annual energy and CO2e results',
        rows: [
          ['IT energy', value(annual.itMWh, 'MWh/year')],
          ['Normal grid-equivalent demand reference', value(annual.normalGridEquivalentDemandMWh, 'MWh/year')],
          ['Comparison grid-equivalent demand reference', value(annual.comparisonGridEquivalentDemandMWh, 'MWh/year')],
          ['Grid imports after declared emergency operation', value(annual.gridImportsAfterDeclaredEmergencyMWh, 'MWh/year')],
          ['Emergency energy-balance check', check(annual.emergencyEnergyBalanceCheck)],
          ['Separate standby testing energy', value(annual.standbyTestingMWh, 'MWh/year')],
          ['Standby testing fuel', value(annual.standbyTestingLitres, 'L/year')],
          ['Normal grid-equivalent CO2e reference', value(annual.normalGridEquivalentTonnesCo2e, 'tCO2e/year')],
          ['Grid CO2e after declared emergency operation', value(annual.gridImportsAfterDeclaredEmergencyTonnesCo2e, 'tCO2e/year')],
          ['Separate standby testing CO2e', value(annual.standbyTestingTonnesCo2e, 'tCO2e/year')],
          ['Normal grid + testing reference subtotal (excludes emergency operation)', value(annual.normalGridAndTestingReferenceSubtotalTonnesCo2e, 'tCO2e/year')],
          ['Declared emergency outage energy', value(annual.declaredEmergencyOutageEnergyMWh, 'MWh/year')],
          ['Emergency outage fuel', value(annual.emergencyOutageLitres, 'L/year')],
          ['Emergency outage CO2e', value(annual.emergencyOutageTonnesCo2e, 'tCO2e/year')],
          ['Complete total including declared emergency operation', value(annual.completeTotalIncludingDeclaredEmergencyTonnesCo2e, 'tCO2e/year')],
          ['Grid displacement from testing', value(annual.gridDisplacementFromTestingMWh, 'MWh/year')],
        ],
      },
      {
        heading: 'Frozen assessment basis',
        rows: [
          ['Model version', projection.modelVersion],
          ['Reviewed document planning fields (estimated)', String(sourceBasis.reviewedDocumentPlanningFields)],
          ['Operator planning assumption fields (estimated)', String(sourceBasis.operatorPlanningAssumptionFields)],
          ['Input SHA-256', projection.inputHash],
          ['Result SHA-256', projection.resultHash],
        ],
      },
    ],
    citations: [...sourceBasis.citations],
    limitations: [...projection.limitations],
    inputHash: projection.inputHash,
    resultHash: projection.resultHash,
  };
}
